/* Route table.

   Backoffice routes are code-split (audit OE-A26): the operator console is a
   different audience entirely, and bundling it into the main chunk taxed
   every tenant page load. `wrap({ asyncComponent })` makes each of them a
   lazy chunk fetched on first backoffice navigation. */

import wrap from "svelte-spa-router/wrap";

import Bootstrap from "./features/bootstrap/Bootstrap.svelte";
import Login from "./features/auth/Login.svelte";
import AcceptInvite from "./features/auth/AcceptInvite.svelte";
import RequestAccess from "./features/auth/RequestAccess.svelte";
import ForgotPassword from "./features/auth/ForgotPassword.svelte";
import ResetPassword from "./features/auth/ResetPassword.svelte";
import ConfirmPasswordChange from "./features/auth/ConfirmPasswordChange.svelte";
import VerifyEmail from "./features/auth/VerifyEmail.svelte";
import NoTenant from "./features/auth/NoTenant.svelte";
import Dashboard from "./features/dashboard/Dashboard.svelte";
import ProductList from "./features/catalog/ProductList.svelte";
import OrderList from "./features/orders/OrderList.svelte";
import Cart from "./features/orders/Cart.svelte";
import WhereToBuyRedirect from "./features/company/WhereToBuyRedirect.svelte";
import AccessMatrix from "./features/pricing/AccessMatrix.svelte";
import DistributorPrices from "./features/pricing/DistributorPrices.svelte";
import PriceFormation from "./features/pricing/PriceFormation.svelte";
import PriceListGenerator from "./features/pricing/PriceListGenerator.svelte";

export const routes: Record<string, any> = {
  "/": Bootstrap,
  "/login": Login,
  "/accept-invite/:token": AcceptInvite,
  "/request-access": RequestAccess,
  // The partner-invitation e-mail's CTA. It pointed here before the route
  // existed, dumping invitees on the login screen with their context lost.
  "/register": RequestAccess,
  "/forgot": ForgotPassword,
  "/reset-password/:token": ResetPassword,
  // The link in the «confirm your new password» e-mail (profile editor).
  "/confirm-password/:token": ConfirmPasswordChange,
  "/verify-email": VerifyEmail,
  // Signed in, but the account belongs to no company (operator accounts, or a
  // membership that was removed). Login/Bootstrap push here.
  "/no-tenant": NoTenant,
  "/t/:slug/account/:tab?": wrap({ asyncComponent: () => import("./features/account/Account.svelte") }),
  "/t/:slug/onboarding": wrap({ asyncComponent: () => import("./features/onboarding/Onboarding.svelte") }),
  "/t/:slug/dashboard": Dashboard,
  "/t/:slug/products": ProductList,
  // «Доступ позиций» is the client tab of Prices since 2026-09-16; old links land there.
  "/t/:slug/item-access": wrap({ asyncComponent: () => import("./features/pricing/Prices.svelte") }),
  "/t/:slug/products/new": wrap({ asyncComponent: () => import("./features/catalog/ProductDetail.svelte") }),
  "/t/:slug/products/import": wrap({ asyncComponent: () => import("./features/catalog/ProductImport.svelte") }),
  // «Категории товаров» left the menu on 2026-09-16: the tree is the first tab
  // of Продукты, and its old URL opens that tab.
  "/t/:slug/product-categories": wrap({ asyncComponent: () => import("./features/catalog/ProductList.svelte") }),
  "/t/:slug/products/:id": wrap({ asyncComponent: () => import("./features/catalog/ProductDetail.svelte") }),
  // «Мои связи» left the menu on 2026-09-16: the map of confirmed partners is
  // the last tab of «Мои партнёры». Its URLs — and those of the sub-tabs it
  // had before that — land on that tab rather than 404-ing.
  "/t/:slug/distribution": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/manufacturers": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/distributors": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/wholesale-catalog": wrap({ asyncComponent: () => import("./features/wholesale/WholesaleCatalog.svelte") }),
  "/t/:slug/orders": OrderList,
  // The short workspace (owner, 2026-09-19): «Ассортимент» is the order form
  // on the inviting supplier's catalogue, «Возможности системы» the page that
  // lays out what is free, what needs the subscription and the free trial.
  "/t/:slug/assortment": wrap({ asyncComponent: () => import("./features/orders/OrderCompose.svelte") }),
  "/t/:slug/features": wrap({ asyncComponent: () => import("./features/features/Features.svelte") }),
  // Composing an order is a page now, not a modal. Both entries must sit
  // above "/orders/:id" — routes match in declaration order, and "new"
  // would otherwise be read as an order code.
  "/t/:slug/orders/new": wrap({ asyncComponent: () => import("./features/orders/OrderCompose.svelte") }),
  // The supplier is part of the address: an unsent order belongs to the
  // supplier it is for, and reloading must come back to the same one.
  "/t/:slug/orders/new/:supplierSlug": wrap({ asyncComponent: () => import("./features/orders/OrderCompose.svelte") }),
  // «Заказ за клиента» — the agent flow: the seller writes down an order the
  // client is not going to type in themselves. Above "/orders/:id" for the
  // same reason "new" is: routes match in declaration order.
  "/t/:slug/orders/for-client": wrap({ asyncComponent: () => import("./features/orders/OrderForClient.svelte") }),
  "/t/:slug/orders/for-client/:draftId": wrap({ asyncComponent: () => import("./features/orders/OrderForClient.svelte") }),
  // A seller draft now has a screen again; the old bookmark opens it there.
  "/t/:slug/orders/draft/:draftId": wrap({ asyncComponent: () => import("./features/orders/OrderForClient.svelte") }),
  "/t/:slug/orders/:id": wrap({ asyncComponent: () => import("./features/orders/OrderDetail.svelte") }),
  "/t/:slug/cart": Cart,
  "/t/:slug/payments": wrap({ asyncComponent: () => import("./features/accounting/Payments.svelte") }),
  // «Бухгалтерия» (month folders) and «Репорт» (owner, 2026-09-19).
  "/t/:slug/accounting/:month?": wrap({ asyncComponent: () => import("./features/accounting/Accounting.svelte") }),
  "/t/:slug/reports/:tab?": wrap({ asyncComponent: () => import("./features/accounting/Reports.svelte") }),
  "/t/:slug/documents": wrap({ asyncComponent: () => import("./features/documents/Documents.svelte") }),
  // Мои партнёры — the merged address book. The old /clients and /suppliers
  // URLs (used by notification e-mails) land on their tab of the same page.
  "/t/:slug/partners": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/suppliers": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/suppliers/:sellerSlug/products": wrap({ asyncComponent: () => import("./features/suppliers/SupplierCatalog.svelte") }),
  "/t/:slug/company-profile": wrap({ asyncComponent: () => import("./features/company/CompanyProfileEditor.svelte") }),
  // Own points of sale left the product ("мой магазин" removed, spec
  // 20260811 §4); the old URL lands on the partner map.
  "/t/:slug/distribution-points": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/clients": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/link-partners": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/link-requests/inbound": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/partner-stores": wrap({ asyncComponent: () => import("./features/linking/MyPartners.svelte") }),
  "/t/:slug/search-requests": wrap({ asyncComponent: () => import("./features/distributors/SearchRequests.svelte") }),
  "/t/:slug/prices": wrap({ asyncComponent: () => import("./features/pricing/Prices.svelte") }),
  // Склад — the shelf, a batch at a time. Sits under Prices in the nav for the
  // same reason it does in the sidebar: it is read while planning what to buy.
  "/t/:slug/stock": wrap({ asyncComponent: () => import("./features/warehouse/Stock.svelte") }),
  // The two ledgers under the shelf: what came in, what we invoiced.
  "/t/:slug/inbound": wrap({ asyncComponent: () => import("./features/warehouse/Inbound.svelte") }),
  // Typing a delivery note in is a page of its own, not a dialog over the
  // ledger: it has a dozen fields and a table, and the people filling it in
  // wanted room (2026-09-10 feedback).
  "/t/:slug/inbound/new": wrap({ asyncComponent: () => import("./features/warehouse/ReceiptEntry.svelte") }),
  "/t/:slug/inbound/:id/edit": wrap({ asyncComponent: () => import("./features/warehouse/ReceiptEntry.svelte") }),
  "/t/:slug/outbound": wrap({ asyncComponent: () => import("./features/warehouse/Outbound.svelte") }),
  // The outbound invoice, on a page laid out like the delivery note (2026-09-18).
  "/t/:slug/outbound/:code/invoice": wrap({ asyncComponent: () => import("./features/warehouse/InvoiceEntry.svelte") }),
  "/t/:slug/pricing": wrap({ asyncComponent: () => import("./features/pricing/PricingPlans.svelte") }),
  "/t/:slug/pricing/access": AccessMatrix,
  "/t/:slug/pricing/distributor-prices": DistributorPrices,
  "/t/:slug/pricing/formation": PriceFormation,
  "/t/:slug/pricing/price-list": PriceListGenerator,
  // Privacy Policy and Terms of Use — anonymous; linked from the login hero,
  // the public company pages and the marketing site.
  "/privacy": wrap({ asyncComponent: () => import("./features/legal/LegalPage.svelte"), props: { kind: "privacy" } }),
  "/terms": wrap({ asyncComponent: () => import("./features/legal/LegalPage.svelte"), props: { kind: "terms" } }),
  // Public partner discovery — anonymous, the registration-growth page.
  "/discover": wrap({ asyncComponent: () => import("./features/discover/PartnerDiscover.svelte") }),
  "/pub/:slug": wrap({ asyncComponent: () => import("./features/company/CompanyPublic.svelte") }),
  "/pub/:slug/products": wrap({ asyncComponent: () => import("./features/company/PublicProducts.svelte") }),
  // The store finder is a section of the company page now; this keeps old
  // links and bookmarks working.
  "/pub/:slug/where-to-buy": WhereToBuyRedirect,
  // Backoffice — platform operations, gated on is_staff / is_super_admin.
  "/backoffice": wrap({ asyncComponent: () => import("./features/backoffice/Overview.svelte") }),
  "/backoffice/users": wrap({ asyncComponent: () => import("./features/backoffice/Users.svelte") }),
  "/backoffice/users/:id": wrap({ asyncComponent: () => import("./features/backoffice/UserDetail.svelte") }),
  "/backoffice/companies": wrap({ asyncComponent: () => import("./features/backoffice/Companies.svelte") }),
  "/backoffice/subscriptions": wrap({ asyncComponent: () => import("./features/backoffice/Subscriptions.svelte") }),
  "/backoffice/payments": wrap({ asyncComponent: () => import("./features/backoffice/Payments.svelte") }),
  "/backoffice/statements": wrap({ asyncComponent: () => import("./features/backoffice/Statements.svelte") }),
  "/backoffice/usage": wrap({ asyncComponent: () => import("./features/backoffice/Usage.svelte") }),
  "/backoffice/price-list": wrap({ asyncComponent: () => import("./features/backoffice/PriceList.svelte") }),
  "/backoffice/services": wrap({ asyncComponent: () => import("./features/backoffice/Services.svelte") }),
  "/backoffice/company-size": wrap({ asyncComponent: () => import("./features/backoffice/CompanySize.svelte") }),
  "/backoffice/access-requests": wrap({ asyncComponent: () => import("./features/backoffice/AccessRequests.svelte") }),
  "/backoffice/characteristics": wrap({
    asyncComponent: () => import("./features/backoffice/Characteristics.svelte"),
  }),
  "/backoffice/orders": wrap({ asyncComponent: () => import("./features/backoffice/Orders.svelte") }),
  "/backoffice/feedback": wrap({ asyncComponent: () => import("./features/backoffice/Feedback.svelte") }),
  "/backoffice/flags": wrap({ asyncComponent: () => import("./features/backoffice/FeatureFlags.svelte") }),
  "/backoffice/role-surfaces": wrap({ asyncComponent: () => import("./features/backoffice/RoleSurfaces.svelte") }),
  "/backoffice/audit": wrap({ asyncComponent: () => import("./features/backoffice/AuditLog.svelte") }),
  "/backoffice/system": wrap({ asyncComponent: () => import("./features/backoffice/System.svelte") }),
  // Superseded by /backoffice; kept so old bookmarks still land somewhere.
  "/admin-portal": wrap({ asyncComponent: () => import("./features/backoffice/LegacyRedirect.svelte") }),
  "/admin-portal/*": wrap({ asyncComponent: () => import("./features/backoffice/LegacyRedirect.svelte") }),
  "*": Login,
};
